/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useSelector } from 'react-redux';

import { Menu, MenuItem, Avatar, Icon } from 'sarsaparilla';

import { csrSelectCustomer, logout } from '../../../auth';
import { useTypedDispatch } from '../../../redux';
import SelectCustomerModal from './selectCustomer/SelectCustomerModal';

import { type ReduxStore } from '../Navbar.types';
import { useGetAvatarUrl, useUserName } from '../Navbar.utils';

import styles from './CSRMenu.module.scss';

/**
 * CSRMenu renders when a Customer Service Representative is impersonating a customer
 * To test:
 * - log into The Hub with a CSR account.
 * - Go to CSR Hub tile
 * - Click on "CSR External Login"
 *
 * To test locally, run ui-navigation with LOCAL_SARSA=true
 * Login as a CSR at localhost:9999/internal/account/login
 * Copy the CSR External Login link and replace r1s-dev.com with localhost:9999
 * Final URL should be like http://localhost:9999/csr-log-in/RANDOM_TOKEN_HERE
 */
export function CSRMenu() {
    const dispatch = useTypedDispatch();

    const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] =
        React.useState(false);

    const auth = useSelector((state: ReduxStore) => state.auth);
    const hasSelectedCustomer = Boolean(auth.account);

    return (
        <>
            <Menu
                label={hasSelectedCustomer ? <CustomerTrigger /> : <CSRTrigger />}
                hasBorder={false}
                hasChevronIcon={true}
            >
                <MenuItem
                    iconBeforeElement={<Icon iconName="person-add" />}
                    onClick={() => {
                        setIsSelectCustomerModalOpen(true);
                    }}
                >
                    {hasSelectedCustomer
                        ? 'Select A Different Customer'
                        : 'Select Customer'}
                </MenuItem>
                <MenuItem
                    appearance="danger"
                    onClick={() => {
                        logout(dispatch);
                    }}
                >
                    CSR Sign Out
                </MenuItem>
            </Menu>

            <SelectCustomerModal
                isOpen={isSelectCustomerModalOpen}
                onRequestClose={() => {
                    setIsSelectCustomerModalOpen(false);
                }}
                csrSelectCustomer={(customer: string) => {
                    csrSelectCustomer(dispatch, customer);
                }}
            />
        </>
    );
}

function CustomerTrigger() {
    const customerName = useUserName();
    const avatarUrl = useGetAvatarUrl();

    return (
        <div className={styles.csrMenu}>
            <Avatar src={avatarUrl} alt={customerName} size="sm" />
            <span>{customerName} (Customer)</span>
        </div>
    );
}

function CSRTrigger() {
    const auth = useSelector((state: ReduxStore) => state.auth);
    const csrName = auth.csrAccount?.name || 'CSR Options';

    return (
        <div className={styles.csrMenu}>
            <Avatar size="sm" className={styles.csrAvatar} />
            <span>{csrName} (CSR)</span>
        </div>
    );
}
